import { FunnelDetails, FunnelMetricsDataType } from './types';
import { getAllSegments } from '../segments/selectors';
import { AppState } from '../../types';
import { createSelector } from 'reselect';
import { EventType } from '@fullstory/search-types';
import { AppCacheStatus } from '@fullstory/redux-util/utils';
import { FunnelDetail } from '@fullstory/proto/fs/services/dx-objects';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { pickBy } from 'lodash';
import { intl } from '@fullstory/intl';
import { createArrayShallowEqualSelector } from '@fullstory/redux-util/utils/selectors';

export const getCandidateSegments = createSelector(getAllSegments, segments =>
  segments.filter(
    segment =>
      segment.segment?.segment?.segmentDefinition?.behaviors
        ?.map(behavior => behavior.event)
        ?.filter(Boolean)?.length,
  ),
);

const getFunnelCreateModalFunnelCounts = (state: AppState) =>
  state.funnels.funnelCreateModal.funnelCounts;

export const getFunnelCountsSelector = (displayFn: (e: EventType) => string) =>
  createSelector(getFunnelCreateModalFunnelCounts, (funnelCounts): FunnelDetails[] =>
    funnelCounts.map((f, i) => ({
      ...f,
      label: f.step
        ? displayFn(f.step)
        : intl.formatMessage(
            {
              defaultMessage: 'Step {stepNum}',
              id: '2GH4kT',
              description:
                'Name for sequential steps a user took in a conversion funnel. e.g step 1, step 2, etc',
            },
            { stepNum: i + 1 },
          ),
    })),
  );

export const getFunnelsAccess = (state: AppState) => state.funnels.access;
export const getSavedFunnelsById = (state: AppState) => state.funnels.funnels;
export const getActiveSavedFunnelsById = createSelector(getSavedFunnelsById, funnels =>
  pickBy(funnels, funnel => !funnel.funnel?.metadata?.archived),
);

export const getAllFunnels = createSelector(getSavedFunnelsById, funnels => Object.values(funnels));

// Alphabetical list of funnels
export const savedFunnelsList = createSelector(getSavedFunnelsById, funnels => {
  const seen = new Set<string>();
  return Object.values(funnels)
    .filter(f => {
      if (f.funnel?.funnel?.id && !seen.has(f.funnel.funnel.id)) {
        seen.add(f.funnel.funnel.id);
        return true;
      }
      return false;
    })
    .sort(nameAndIdCompare);
});

export const activeFunnelsList = createSelector(savedFunnelsList, funnels =>
  funnels.filter(f => !f.funnel?.metadata?.archived),
);

export const getStarredFunnels = createSelector(savedFunnelsList, funnels =>
  funnels.filter(f => f.starred),
);

const nameAndIdCompare = (a: FunnelDetail, b: FunnelDetail) =>
  a.funnel?.funnel?.name
    ?.toLowerCase()
    .localeCompare(b.funnel?.funnel?.name?.toLowerCase() ?? '') ||
  a.funnel?.funnel?.id.localeCompare(b.funnel?.funnel?.id ?? '') ||
  0;

export const recentFunnelsList = createSelector(getSavedFunnelsById, funnels => {
  const list: FunnelDetail[] = [];
  for (const key in funnels) {
    const funnel = funnels[key];
    if (funnel?.lastViewed) list.push(funnel);
  }
  return list.sort((a, b) => Timestamp.toMillis(b.lastViewed) - Timestamp.toMillis(a.lastViewed));
});

export interface FunnelMetricsCacheData {
  status: AppCacheStatus;
  ready: boolean;
  data: FunnelMetricsDataType | null;
  storageId: string;
}

// Data selectors
const getFunnelsDataMap = (state: AppState) => state.funnels.funnelMetrics;

type FunnelsDataMap = ReturnType<typeof getFunnelsDataMap>;

export const makeGetFunnelsDataArray = (storageIds: string[]) =>
  createArrayShallowEqualSelector(
    createSelector(getFunnelsDataMap, (map: FunnelsDataMap): FunnelMetricsCacheData[] =>
      storageIds.map(s => getFunnelsData(map, s)),
    ),
    dataArray => dataArray,
  );

function getFunnelsData(map: FunnelsDataMap, storageId: string) {
  const { status = AppCacheStatus.Initial, ready = false, data = null } = map[storageId] ?? {};

  return { status, ready, data, storageId };
}
